import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const EditIcon = () => (
  <svg className="w-[18px] h-[18px] md:w-[22px] md:h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-[18px] h-[18px] md:w-[22px] md:h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 flex-shrink-0">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const PlayIcon = () => (
  <svg className="w-[18px] h-[18px] md:w-[22px] md:h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const ResultsIcon = () => (
  <svg className="w-[18px] h-[18px] md:w-[22px] md:h-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

const RequestsList = ({ requests, onEdit, onDelete, onRunNow }) => {
  const [runningId, setRunningId] = useState(null);

  const handleRunNow = async (id) => {
    setRunningId(id);
    try {
      await onRunNow(id);
    } finally {
      setRunningId(null);
    }
  };

  if (!requests.length) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No research requests yet. Create one to get started.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {requests.map((r) => (
        <div
          key={r.id}
          className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition"
        >
          {/* Top row: name + icon actions */}
          <div className="flex items-start justify-between gap-2">
            <Link
              to={`/requests/${r.id}`}
              className="font-semibold text-base text-gray-900 hover:text-primary-600 flex-1 min-w-0 leading-snug"
            >
              {r.name}
            </Link>
            <div className="flex gap-1 flex-shrink-0 -mt-0.5">
              <Link
                to={`/results?request_id=${r.id}`}
                className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition"
                title="View Results"
              >
                <ResultsIcon />
              </Link>
              <button
                onClick={() => handleRunNow(r.id)}
                disabled={runningId === r.id}
                className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
                title="Run Now"
              >
                {runningId === r.id ? (
                  <svg className="animate-spin w-[18px] h-[18px] md:w-[22px] md:h-[22px]" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <PlayIcon />
                )}
              </button>
              <button
                onClick={() => onEdit(r)}
                className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
                title="Edit"
              >
                <EditIcon />
              </button>
              <button
                onClick={() => onDelete(r.id)}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                title="Delete"
              >
                <TrashIcon />
              </button>
            </div>
          </div>

          {/* Prompt */}
          {Boolean(r.mask_prompt) ? (
            <p className="text-sm text-gray-400 mt-1.5 italic">Prompt hidden</p>
          ) : (
            <p className="text-sm text-gray-500 mt-1.5 line-clamp-2">
              {r.prompt.length > 120 ? r.prompt.slice(0, 120) + '…' : r.prompt}
            </p>
          )}

          {/* Tags */}
          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
              {r.frequency === 'weekdays' ? 'Weekdays' : r.frequency}
            </span>
            {r.time && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                {r.frequency === 'weekly' && r.day_of_week != null
                  ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][r.day_of_week] + ' '
                  : ''}
                {r.time} UTC
              </span>
            )}
            {Boolean(r.email_notify) && r.email_address && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 truncate max-w-[180px]">
                {r.email_address}
              </span>
            )}
          </div>

          {/* Next run */}
          <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500">
            <ClockIcon />
            <span>
              Next run:{' '}
              <span className="font-medium text-gray-700">
                {r.next_run_time ? new Date(r.next_run_time).toLocaleString() : '—'}
              </span>
            </span>
          </div>

        </div>
      ))}
    </div>
  );
};

export default RequestsList;
